// eslint-disable-next-line eslint-comments/disable-enable-pair -- Ignore
/* eslint-disable react/jsx-props-no-spreading -- we are in a HOC, it makes sense to spread here */
import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connectAsync } from 'iguazu';
import { queryResource } from 'iguazu-rest';
import { connect } from 'react-redux';
import getByPath from '../utils/getByPath';

export const getComponentName = (WrappedComponent) => `WithOneCmsJson(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;

export const getPropsFromJson = (mapOneCmsJsonToProps, oneCmsJson) => Object
  .keys(mapOneCmsJsonToProps)
  .reduce((newProps, propKey) => ({
    ...newProps,
    [propKey]: getByPath(oneCmsJson, mapOneCmsJsonToProps[propKey]),
  }), {});

const withOneCmsJson = (WrappedComponent) => {
  const WithOneCmsJsonHOC = (props) => {
    // We remove props generated by the HOC that we either need i.e hocOneCmsData
    // or don't want to pass through i.e iguazu isLoading
    const {
      hocOneCmsData: oneCmsData,
      isLoading,
      loadedWithErrors,
      loadErrors,
      loadStatus,
      mapOneCmsJsonToProps,
      ...passThroughProps
    } = props;
    let hocProps = {};

    if (!isLoading() && !loadedWithErrors()) {
      hocProps = mapOneCmsJsonToProps
        ? getPropsFromJson(mapOneCmsJsonToProps, oneCmsData)
        : { oneCmsData };
    }

    return (
      <WrappedComponent
        {...passThroughProps}
        {...hocProps}
        oneCmsHasLoadErrors={loadedWithErrors()}
        oneCmsIsLoading={isLoading()}
      />
    );
  };

  WithOneCmsJsonHOC.propTypes = {
    hocOneCmsData: PropTypes.shape({}),
    isLoading: PropTypes.func.isRequired,
    loadedWithErrors: PropTypes.func.isRequired,
    loadErrors: PropTypes.shape({
      hocOneCmsData: PropTypes.bool,
    }),
    loadStatus: PropTypes.shape({
      hocOneCmsData: PropTypes.bool,
    }),
    mapOneCmsJsonToProps: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.func]),
  };
  WithOneCmsJsonHOC.defaultProps = {
    mapOneCmsJsonToProps: null,
    hocOneCmsData: null,
    loadErrors: undefined,
    loadStatus: undefined,
  };

  WithOneCmsJsonHOC.displayName = getComponentName(WrappedComponent);

  return WithOneCmsJsonHOC;
};

export { withOneCmsJson as testableWithProgramHotel };

export const generateLoadDataAsPropsForOneCmsURL = (generateUrlSelector) => ({
  store: { dispatch, getState },
}) => {
  if (generateUrlSelector) {
    return {
      hocOneCmsData: () => dispatch(queryResource({
        resource: 'oneCMSInternetContent',
        id: generateUrlSelector(getState()),
      })),
    };
  }
  console.error('Attempted to use withOneCmsJson but didn\'t provide a URL generator');
  return {};
};

export const mapStateToHocParamFunction = (mapOneCmsJsonToProps) => (state, ownProps) => {
  if (mapOneCmsJsonToProps) {
    if (typeof mapOneCmsJsonToProps === 'function') {
      return {
        mapOneCmsJsonToProps: mapOneCmsJsonToProps(state, ownProps),
      };
    }
    return {
      mapOneCmsJsonToProps,
    };
  }
  return null;
};

const hocChain = (generateUrlSelector, mapOneCmsJsonToProps) => compose(
  connect(mapStateToHocParamFunction(mapOneCmsJsonToProps)),
  connectAsync({ loadDataAsProps: generateLoadDataAsPropsForOneCmsURL(generateUrlSelector) })
);

const hocWrapped = (
  generateUrlSelector,
  mapOneCmsJsonToProps
) => (WrappedComponent) => hocChain(generateUrlSelector, mapOneCmsJsonToProps)(
  withOneCmsJson(WrappedComponent)
);

export { withOneCmsJson as testableWithOneCmsJson };

export default hocWrapped;
